import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import config from "../Config/config";

const AGREEMENT_TITLE = "Shipping & Fulfillment Agreement";
const AGREEMENT_PARAGRAPHS = [
  "All winning bids are final and create a legal obligation to pay the auction invoice. After auction close, Buyer must select Pickup or Shipping.",
  "If Shipping is selected, Shipping Saint will issue a separate invoice for actual shipping and handling charges. Shipping charges are not included in the auction invoice.",
  "Failure to select Shipping or Pickup does not void the sale. Items not paid for or not removed within the designated window may be deemed abandoned and subject to restocking, storage, or resale.",
  "By bidding and confirming fulfillment, Buyer agrees to these terms.",
];
const SIGNATURE_CANVAS_WIDTH = 760;
const SIGNATURE_CANVAS_HEIGHT = 190;

const normalizeEmailParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .toLowerCase();
const normalizeOrgParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .replace(/[^a-zA-Z0-9_-]/g, "");
const normalizeRegistrationParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "");

const fmtDateTime = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "—";
  try {
    return new Date(Number(ms)).toLocaleString();
  } catch {
    return "—";
  }
};

export default function ShippingAgreementPage() {
  const [email, setEmail] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [auctionId, setAuctionId] = useState("");
  const [auctionName, setAuctionName] = useState("");
  const [username, setUsername] = useState("");
  const [agreementText, setAgreementText] = useState(AGREEMENT_PARAGRAPHS.join("\n"));
  const signatureCanvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [bidderName, setBidderName] = useState("");
  const [policy, setPolicy] = useState({
    optIn: false,
    signatureName: "",
    signatureDataUrl: "",
    signedAtMs: null,
    lastActionAtMs: null,
  });
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [signatureName, setSignatureName] = useState("");
  const [signatureHasInk, setSignatureHasInk] = useState(false);

  const getCanvasContext = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return { canvas: null, ctx: null };
    const ctx = canvas.getContext("2d");
    if (!ctx) return { canvas: null, ctx: null };
    return { canvas, ctx };
  };
  const resetSignatureCanvas = () => {
    const { canvas, ctx } = getCanvasContext();
    if (!canvas || !ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    drawingRef.current = false;
    setSignatureHasInk(false);
  };
  const drawSignatureImage = (dataUrl) => {
    const src = String(dataUrl || "").trim();
    if (!src) {
      resetSignatureCanvas();
      return;
    }
    const { canvas, ctx } = getCanvasContext();
    if (!canvas || !ctx) return;
    const img = new Image();
    img.onload = () => {
      resetSignatureCanvas();
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setSignatureHasInk(true);
    };
    img.onerror = () => {
      resetSignatureCanvas();
    };
    img.src = src;
  };
  const getPointerPos = (evt) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const touch = evt?.touches?.[0] || evt?.changedTouches?.[0] || null;
    const clientX = touch ? touch.clientX : evt.clientX;
    const clientY = touch ? touch.clientY : evt.clientY;
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };
  const startDraw = (evt) => {
    const { ctx } = getCanvasContext();
    if (!ctx) return;
    const p = getPointerPos(evt);
    if (!p) return;
    drawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    if (evt.cancelable) evt.preventDefault();
  };
  const moveDraw = (evt) => {
    if (!drawingRef.current) return;
    const { ctx } = getCanvasContext();
    if (!ctx) return;
    const p = getPointerPos(evt);
    if (!p) return;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setSignatureHasInk((prev) => prev || true);
    if (evt.cancelable) evt.preventDefault();
  };
  const endDraw = () => {
    drawingRef.current = false;
  };
  const captureSignatureDataUrl = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !signatureHasInk) return "";
    try {
      return canvas.toDataURL("image/png");
    } catch {
      return "";
    }
  };

  useEffect(() => {
    resetSignatureCanvas();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailFromUrl = normalizeEmailParam(params.get("email"));
    const orgFromUrl = normalizeOrgParam(params.get("org") || params.get("orgId") || params.get("publicOrgId"));
    const registrationFromUrl = normalizeRegistrationParam(params.get("registrationId") || params.get("regId"));
    const auctionFromUrl = String(params.get("auctionId") || "").trim();
    if (!orgFromUrl) {
      setError("Missing organization key in link. Use ?org=<key>&registrationId=<docId>.");
      setLoading(false);
      return;
    }
    if (!registrationFromUrl && (!auctionFromUrl || !emailFromUrl)) {
      setError("Missing registrationId in link (or provide auctionId + email).");
      setLoading(false);
      return;
    }
    if (!registrationFromUrl && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailFromUrl)) {
      setError("Invalid email in link.");
      setLoading(false);
      return;
    }

    setOrgId(orgFromUrl);
    setEmail(emailFromUrl);
    setRegistrationId(registrationFromUrl);
    setAuctionId(auctionFromUrl);
    void loadStatus({
      org: orgFromUrl,
      email: emailFromUrl,
      registrationId: registrationFromUrl,
      auctionId: auctionFromUrl,
    });
  }, []);

  const statusLabel = useMemo(() => {
    if (!exists) return "Registration Not Found";
    return policy?.optIn ? "Signed" : "Pending Signature";
  }, [exists, policy?.optIn]);
  const agreementParagraphs = useMemo(() => {
    const raw = String(agreementText || "").replace(/\r\n?/g, "\n").trim();
    if (!raw) return AGREEMENT_PARAGRAPHS;
    const lines = raw
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return lines.length ? lines : AGREEMENT_PARAGRAPHS;
  }, [agreementText]);

  const loadStatus = async ({
    org,
    email: emailValue,
    registrationId: registrationIdValue = "",
    auctionId: auctionIdValue = "",
  }) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/shipping-policy/status`, {
        params: {
          org,
          ...(emailValue ? { email: emailValue } : {}),
          ...(registrationIdValue ? { registrationId: registrationIdValue } : {}),
          ...(auctionIdValue ? { auctionId: auctionIdValue } : {}),
        },
      });

      if (!data?.ok) throw new Error(data?.error || "Failed to load status");
      const existsNow = !!data?.exists;
      const nextPolicy = data?.shipping || data?.shippingPolicy || {};
      const nextOptIn = nextPolicy?.optIn === true || nextPolicy?.optedIn === true;

      setExists(existsNow);
      setBidderName(String(data?.bidderName || "").trim());
      setUsername(String(data?.username || "").trim());
      setAuctionName(String(data?.auctionName || "").trim());
      setAuctionId(String(data?.auctionId || auctionIdValue || "").trim());
      setRegistrationId(String(data?.registrationId || registrationIdValue || "").trim());
      setEmail(String(data?.email || emailValue || "").trim().toLowerCase());
      setPolicy({
        optIn: nextOptIn,
        signatureName: String(nextPolicy?.signatureName || "").trim(),
        signatureDataUrl: String(nextPolicy?.signatureDataUrl || "").trim(),
        signedAtMs: nextPolicy?.signedAtMs || null,
        lastActionAtMs: nextPolicy?.lastActionAtMs || null,
      });
      setOrgId(String(data?.publicOrgId || org || "").trim());
      setOrgName(String(data?.orgName || "").trim());
      setAgreementText(String(data?.agreement?.text || AGREEMENT_PARAGRAPHS.join("\n")).trim());

      const signatureSeed = String(nextPolicy?.signatureName || data?.bidderName || "").trim();
      setSignatureName(signatureSeed);
      if (!existsNow) setError("No registration was found for this shipping link.");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to load shipping agreement status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!exists) {
      resetSignatureCanvas();
      return;
    }
    drawSignatureImage(policy?.signatureDataUrl || "");
  }, [exists, policy?.signatureDataUrl]);

  const submitDecision = async () => {
    if (!orgId || (!registrationId && (!auctionId || !email))) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const signatureDataUrl = captureSignatureDataUrl();
      if (String(signatureName || "").trim().length < 2) {
        throw new Error("Type your full name before signing.");
      }
      if (!signatureDataUrl) {
        throw new Error("Draw your signature in the canvas before signing.");
      }

      const payload = {
        org: orgId,
        email,
        registrationId: registrationId || "",
        auctionId: auctionId || "",
        optIn: true,
        optedIn: true,
        signatureName: String(signatureName || "").trim(),
        signatureDataUrl,
      };
      const { data } = await axios.post(`${config.API_BASE_URL}/shipping-policy/submit`, payload);
      if (!data?.ok) throw new Error(data?.error || "Failed to submit.");

      const nextPolicy = data?.shipping || data?.shippingPolicy || {};
      const nextOptIn = nextPolicy?.optIn === true || nextPolicy?.optedIn === true;
      setExists(!!data?.exists);
      setBidderName(String(data?.bidderName || "").trim());
      setUsername(String(data?.username || "").trim());
      setAuctionName(String(data?.auctionName || "").trim());
      setAuctionId(String(data?.auctionId || auctionId || "").trim());
      setRegistrationId(String(data?.registrationId || registrationId || "").trim());
      setEmail(String(data?.email || email || "").trim().toLowerCase());
      setPolicy({
        optIn: nextOptIn,
        signatureName: String(nextPolicy?.signatureName || "").trim(),
        signatureDataUrl: String(nextPolicy?.signatureDataUrl || "").trim(),
        signedAtMs: nextPolicy?.signedAtMs || null,
        lastActionAtMs: nextPolicy?.lastActionAtMs || null,
      });
      setOrgId(String(data?.publicOrgId || orgId || "").trim());
      setOrgName(String(data?.orgName || "").trim());
      setAgreementText(String(data?.agreement?.text || agreementText || AGREEMENT_PARAGRAPHS.join("\n")).trim());
      setSuccess("Shipping agreement signed successfully.");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to submit your signature.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg,#f8fafc 0%,#eef2ff 100%)",
        px: 2,
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        <Paper
          sx={{
            borderRadius: 3,
            border: "1px solid #dbe2ef",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box sx={{ p: 2.75, background: "linear-gradient(100deg,#1e3a8a,#1d4ed8 58%,#2563eb)" }}>
            <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 24 }}>{AGREEMENT_TITLE}</Typography>
            {orgName ? (
              <Typography sx={{ color: "rgba(255,255,255,0.92)", mt: 0.5, fontWeight: 700 }}>
                {orgName}
              </Typography>
            ) : null}
            <Typography sx={{ color: "rgba(255,255,255,0.9)", mt: 0.5 }}>
              Review and sign your shipping agreement for this auction registration.
            </Typography>
          </Box>

          <Box sx={{ p: 2.75, backgroundColor: "#fff" }}>
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2.25, p: 1.6, mb: 2, borderColor: "#d9e1f2", background: "#f8fbff" }}
            >
              <Typography sx={{ color: "#1e3a8a", fontWeight: 900, mb: 1.2, fontSize: 14, letterSpacing: 0.3 }}>
                Registration Snapshot
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                }}
              >
                {[
                  { label: "Status", value: statusLabel || "—" },
                  { label: "Bidder Name", value: bidderName || "—" },
                  { label: "Username", value: username || "—" },
                  { label: "Email", value: email || "—" },
                  { label: "Auction", value: auctionName || auctionId || "—" },
                  { label: "Organization", value: orgName || orgId || "—" },
                ].map((item) => {
                  const isStatus = item.label === "Status";
                  const statusPositive = isStatus && exists && policy?.optIn === true;
                  const statusNegative = isStatus && !statusPositive;
                  return (
                  <Box
                    key={item.label}
                    sx={{
                      border: "1px solid",
                      borderColor: statusPositive ? "#86efac" : statusNegative ? "#fca5a5" : "#dbe6fb",
                      borderRadius: 1.5,
                      p: 1.15,
                      minHeight: 68,
                      background: statusPositive ? "#f0fdf4" : statusNegative ? "#fef2f2" : "#ffffff",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280", mb: 0.45 }}>
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: statusPositive ? "#166534" : statusNegative ? "#991b1b" : "#0f172a",
                        fontWeight: 800,
                        fontSize: 14.2,
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                  );
                })}
              </Box>
            </Paper>

            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {error ? (
                  <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
                    {error}
                  </Alert>
                ) : null}
                {success ? (
                  <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
                    {success}
                  </Alert>
                ) : null}

                <Typography sx={{ color: "#1f2937", fontWeight: 900, mb: 1, fontSize: 15 }}>
                  Shipping & Fulfillment Agreement Content
                </Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, backgroundColor: "#f3f4f6", borderColor: "#d1d5db" }}>
                  {agreementParagraphs.map((line) => (
                    <Typography key={line} sx={{ color: "#111827", mb: 1.35, lineHeight: 1.6 }}>
                      {line}
                    </Typography>
                  ))}
                </Paper>

                {exists ? (
                  <Box sx={{ mt: 2.25 }}>
                    <Typography sx={{ fontWeight: 800, color: "#111827", mb: 1.25 }}>Signature</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="Type your full name"
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      disabled={policy?.optIn || submitting}
                      sx={{ backgroundColor: "#fff", borderRadius: 2, mb: 1.5 }}
                    />
                    <Typography variant="body2" sx={{ color: "#374151", mb: 0.75 }}>
                      {policy?.optIn ? "Signed signature shown below." : "Draw signature in the box below."}
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ borderRadius: 2, p: 1, backgroundColor: "#fff", borderColor: "#cbd5e1", mb: 1 }}
                    >
                      <canvas
                        ref={signatureCanvasRef}
                        width={SIGNATURE_CANVAS_WIDTH}
                        height={SIGNATURE_CANVAS_HEIGHT}
                        style={{
                          width: "100%",
                          height: 190,
                          border: "1px dashed #94a3b8",
                          borderRadius: 8,
                          backgroundColor: "#ffffff",
                          touchAction: "none",
                          cursor: policy?.optIn ? "default" : "crosshair",
                        }}
                        onMouseDown={policy?.optIn ? undefined : startDraw}
                        onMouseMove={policy?.optIn ? undefined : moveDraw}
                        onMouseUp={policy?.optIn ? undefined : endDraw}
                        onMouseLeave={policy?.optIn ? undefined : endDraw}
                        onTouchStart={policy?.optIn ? undefined : startDraw}
                        onTouchMove={policy?.optIn ? undefined : moveDraw}
                        onTouchEnd={policy?.optIn ? undefined : endDraw}
                      />
                    </Paper>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                      <Button
                        variant="text"
                        onClick={resetSignatureCanvas}
                        disabled={submitting || policy?.optIn}
                        sx={{ textTransform: "none", fontWeight: 900 }}
                      >
                        Clear Signature
                      </Button>
                      <Chip
                        size="small"
                        label={signatureHasInk ? "Signature captured" : "Please sign in canvas"}
                        sx={{ borderRadius: 999, fontWeight: 800 }}
                      />
                    </Box>

                    {policy?.optIn ? (
                      <Typography variant="body2" sx={{ color: "#374151", mb: 1.5 }}>
                        You have already signed. You can close this tab.
                        {policy?.signedAtMs ? ` Signed at: ${fmtDateTime(policy.signedAtMs)}.` : ""}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#374151", mb: 1.5 }}>
                        Please sign to confirm this shipping agreement.
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        disabled={
                          policy?.optIn ||
                          submitting ||
                          String(signatureName || "").trim().length < 2 ||
                          !signatureHasInk
                        }
                        onClick={submitDecision}
                        sx={{ textTransform: "none", borderRadius: 999, fontWeight: 900 }}
                      >
                        {policy?.optIn ? "You have signed" : submitting ? "Saving..." : "Sign Agreement"}
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={submitting}
                        onClick={() =>
                          loadStatus({
                            org: orgId,
                            email,
                            registrationId,
                            auctionId,
                          })
                        }
                        sx={{ textTransform: "none", borderRadius: 999, fontWeight: 900 }}
                      >
                        Refresh
                      </Button>
                    </Box>
                  </Box>
                ) : null}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
